'use client'
import React, { useCallback, useEffect } from 'react'
import { TextFieldClientProps } from 'payload'

import { useField, Button, TextInput, FieldLabel, useFormFields, useForm } from '@payloadcms/ui'

import { formatSlug, buildSlugFromTitleSuffixYear } from './formatSlug'
import type { SlugFromFieldsConfig } from './formatSlug'
import './index.scss'

type SlugComponentProps = {
  fieldToUse?: string
  slugFromFields?: SlugFromFieldsConfig
  checkboxFieldPath: string
} & TextFieldClientProps

export const SlugComponent: React.FC<SlugComponentProps> = ({
  field,
  fieldToUse,
  slugFromFields,
  checkboxFieldPath: checkboxFieldPathFromProps,
  path,
  readOnly: readOnlyFromProps,
}) => {
  const { label } = field

  const checkboxFieldPath = path?.includes('.')
    ? `${path}.${checkboxFieldPathFromProps}`
    : checkboxFieldPathFromProps

  const { value, setValue } = useField<string>({ path: path || field.name })

  const { dispatchFields } = useForm()

  const checkboxValue = useFormFields(([fields]) => {
    return fields[checkboxFieldPath]?.value as string
  })

  const multiFieldValues = useFormFields(([fields]) => {
    if (!slugFromFields) return null
    const title = fields[slugFromFields.titleField]?.value as string | undefined
    const suffix = slugFromFields.suffixField
      ? (fields[slugFromFields.suffixField]?.value as string | undefined)
      : undefined
    const date = slugFromFields.dateField
      ? (fields[slugFromFields.dateField]?.value as string | Date | null)
      : null
    return { title, suffix, date }
  })

  const singleFieldValue = useFormFields(([fields]) => {
    return fieldToUse ? (fields[fieldToUse]?.value as string) : undefined
  })

  useEffect(() => {
    if (!checkboxValue) return
    if (slugFromFields && multiFieldValues) {
      const { title, suffix, date } = multiFieldValues
      const built = buildSlugFromTitleSuffixYear(title, suffix, date)
      if (built && value !== built) setValue(built)
      else if (!built && value !== '') setValue('')
    } else if (fieldToUse && singleFieldValue !== undefined) {
      if (singleFieldValue) {
        const formattedSlug = formatSlug(singleFieldValue)
        if (value !== formattedSlug) setValue(formattedSlug)
      } else if (value !== '') setValue('')
    }
  }, [checkboxValue, slugFromFields, multiFieldValues, fieldToUse, singleFieldValue, value, setValue])

  const handleLock = useCallback(
    (e: React.MouseEvent<Element>) => {
      e.preventDefault()

      dispatchFields({
        type: 'UPDATE',
        path: checkboxFieldPath,
        value: !checkboxValue,
      })
    },
    [checkboxValue, checkboxFieldPath, dispatchFields],
  )

  const readOnly = readOnlyFromProps || checkboxValue

  return (
    <div className="field-type slug-field-component">
      <div className="label-wrapper">
        <FieldLabel htmlFor={`field-${path}`} label={label} />

        <Button className="lock-button" buttonStyle="none" onClick={handleLock}>
          {checkboxValue ? 'Unlock' : 'Lock'}
        </Button>
      </div>

      <TextInput
        value={value}
        onChange={setValue}
        path={path || field.name}
        readOnly={Boolean(readOnly)}
      />
    </div>
  )
}
